import { useEffect, useRef, useState } from "react";

type Options = {
  threshold?: number;
  topLock?: number;
  bottomLock?: number;
};

export function useAutoHideHeader({
  threshold = 10,
  topLock = 8,
  bottomLock = 8,
}: Options = {}) {
  const [hidden, setHidden] = useState(false);

  const lastY = useRef(0);
  const ticking = useRef(false);

  const getMaxScroll = () =>
    Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

  const clampY = (y: number) => {
    const max = getMaxScroll();
    if (y < 0) return 0; // iOS top bounce
    if (y > max) return max; // iOS bottom bounce
    return y;
  };

  useEffect(() => {
    const update = () => {
      const rawY = window.scrollY;
      const y = clampY(rawY);
      const max = getMaxScroll();

      // 최상단 근처 - 항상 보이기
      if (y <= topLock) {
        setHidden(false);
        lastY.current = y;
        return;
      }

      // 최하단 근처 - 항상 숨기기
      if (max - y <= bottomLock) {
        setHidden(true);
        lastY.current = y;
        return;
      }

      // 방향 판정
      const diff = y - lastY.current;
      if (Math.abs(diff) < threshold) return;

      setHidden(diff > 0); // down -> hide, up -> show
      lastY.current = y;
    };

    const onScroll = () => {
      if (ticking.current) return;

      ticking.current = true;
      requestAnimationFrame(() => {
        update();
        ticking.current = false;
      });
    };

    // 초기값 세팅
    lastY.current = clampY(window.scrollY);
    update();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", update);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", update);
    };
  }, [threshold, topLock, bottomLock]);

  return hidden;
}
